(() => {
  const findAll = (root, selector) => {
    const matches = root?.matches?.(selector) ? [root] : [];
    return [...matches, ...(root?.querySelectorAll?.(selector) || [])];
  };
  let reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.matchMedia('(prefers-reduced-motion: reduce)').addEventListener('change', event => { reduceMotion = event.matches; });
  const saveData = Boolean(navigator.connection?.saveData);
  const loadYouTubePlayerApi = () => {
    if (window.YT?.Player) return Promise.resolve(window.YT);
    if (window.__stillhighYouTubeApiPromise) return window.__stillhighYouTubeApiPromise;

    window.__stillhighYouTubeApiPromise = new Promise((resolve, reject) => {
      let settled = false;
      let pollTimer = null;
      let timeoutTimer = null;
      const finish = () => {
        if (settled || !window.YT?.Player) return;
        settled = true;
        window.clearInterval(pollTimer);
        window.clearTimeout(timeoutTimer);
        resolve(window.YT);
      };
      const fail = () => {
        if (settled) return;
        settled = true;
        window.clearInterval(pollTimer);
        window.clearTimeout(timeoutTimer);
        reject(new Error('YouTube player API unavailable'));
      };
      let script = document.querySelector('script[src*="youtube.com/iframe_api"]');
      if (!script) {
        script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        script.async = true;
        script.dataset.shYoutubeApi = 'true';
        document.head.append(script);
      }
      script.addEventListener('error', fail, { once: true });
      pollTimer = window.setInterval(finish, 100);
      timeoutTimer = window.setTimeout(fail, 12000);
      finish();
    }).catch((error) => {
      delete window.__stillhighYouTubeApiPromise;
      throw error;
    });

    return window.__stillhighYouTubeApiPromise;
  };

  const initRotators = (root) => {
    findAll(root, '[data-sh-rotator]').forEach((rotator) => {
      if (rotator.dataset.ready === 'true') return;
      rotator.dataset.ready = 'true';
      const slides = [...rotator.querySelectorAll('[data-sh-rotator-video]')];
      const mobileVideo = rotator.querySelector('.sh-gateway__mobile-media video');
      if (!slides.length && !mobileVideo) return;
      const mobileQuery = window.matchMedia('(max-width: 749px)');
      const toggles = [...rotator.querySelectorAll('[data-sh-rotator-toggle]')];
      const transmissionCount = rotator.querySelector('[data-sh-rotator-count]');
      const transmissionLabel = rotator.querySelector('[data-sh-rotator-current]');
      const progressSegments = [...rotator.querySelectorAll('[data-sh-rotator-progress] i')];
      const paintTransmission = (index, total, label) => {
        if (transmissionCount) transmissionCount.textContent = `TRANSMISSION ${String(index + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
        if (transmissionLabel) transmissionLabel.textContent = label || `STILLHIGH ORIGINAL ${String(index + 1).padStart(2, '0')}`;
        progressSegments.forEach((segment, segmentIndex) => segment.classList.toggle('is-active', segmentIndex === index));
      };
      let paused = reduceMotion || saveData;
      try { paused = paused || localStorage.getItem('stillhigh-atmosphere-paused') === 'true'; } catch (_) {}
      let inView = true;
      let timer = null;
      let active = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      slides.forEach((slide, index) => slide.classList.toggle('is-active', index === active));
      const speed = Math.max(4500, Number(rotator.dataset.speed || 8500));
      const mobileMode = () => Boolean(mobileVideo && mobileQuery.matches);
      const clearSchedule = () => {
        if (timer) window.clearTimeout(timer);
        timer = null;
      };
      const stopAll = () => {
        mobileVideo?.pause?.();
        slides.forEach((slide) => slide.querySelector('video')?.pause?.());
      };
      const canPlay = () => !paused && inView && !document.hidden;
      const paintActiveTransmission = () => {
        if (mobileMode()) {
          paintTransmission(0, 1, rotator.querySelector('[data-sh-rotator-mobile-label]')?.dataset.shRotatorMobileLabel);
        } else {
          paintTransmission(active, Math.max(1, slides.length), slides[active]?.dataset.shRotatorLabel);
        }
      };
      const schedule = () => {
        clearSchedule();
        if (!mobileMode() && slides.length > 1 && canPlay()) timer = window.setTimeout(advance, speed);
      };
      const refreshPlayback = () => {
        clearSchedule();
        stopAll();
        paintActiveTransmission();
        if (canPlay()) {
          const video = mobileMode() ? mobileVideo : slides[active]?.querySelector('video');
          video?.play?.().catch(() => {});
        }
        schedule();
      };
      function advance() {
        if (mobileMode() || !canPlay() || !slides.length) { refreshPlayback(); return; }
        slides[active]?.classList.remove('is-active');
        active = (active + 1) % slides.length;
        slides[active]?.classList.add('is-active');
        refreshPlayback();
      }
      const refreshToggle = () => {
        rotator.dataset.shMotionPaused = paused ? 'true' : 'false';
        document.documentElement.dataset.shAtmospherePaused = paused ? 'true' : 'false';
        toggles.forEach((button) => {
          button.setAttribute('aria-pressed', paused ? 'true' : 'false');
          const label = button.querySelector('[data-sh-motion-label]');
          if (label) label.textContent = paused ? 'PLAY ATMOSPHERE' : 'PAUSE ATMOSPHERE';
        });
      };
      const toggleMotion = () => {
        paused = !paused;
        try { localStorage.setItem('stillhigh-atmosphere-paused', paused ? 'true' : 'false'); } catch (_) {}
        refreshToggle();
        refreshPlayback();
        window.dispatchEvent(new CustomEvent('stillhigh:motionchange', { detail: { paused } }));
      };
      toggles.forEach((button) => button.addEventListener('click', toggleMotion));
      const visibility = () => refreshPlayback();
      const modeChange = () => refreshPlayback();
      document.addEventListener('visibilitychange', visibility);
      if (mobileQuery.addEventListener) mobileQuery.addEventListener('change', modeChange);
      else mobileQuery.addListener?.(modeChange);
      const viewportObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
        inView = entries[0]?.isIntersecting ?? true;
        refreshPlayback();
      }, { rootMargin: '80px 0px', threshold: .04 }) : null;
      viewportObserver?.observe(rotator);
      refreshToggle();
      paintActiveTransmission();
      refreshPlayback();
      rotator._shCleanup = () => {
        clearSchedule();
        viewportObserver?.disconnect();
        document.removeEventListener('visibilitychange', visibility);
        if (mobileQuery.removeEventListener) mobileQuery.removeEventListener('change', modeChange);
        else mobileQuery.removeListener?.(modeChange);
        toggles.forEach((button) => button.removeEventListener('click', toggleMotion));
        stopAll();
      };
    });
  };

  const initPrompts = (root) => {
    findAll(root, '[data-sh-prompt]').forEach((button) => {
      if (button.dataset.ready === 'true') return;
      button.dataset.ready = 'true';
      button.setAttribute('aria-pressed', button.classList.contains('is-active') ? 'true' : 'false');
      button.addEventListener('click', () => {
        const lab = button.closest('[data-sh-lab]');
        if (!lab) return;
        lab.querySelectorAll('[data-sh-prompt]').forEach((item) => {
          item.classList.remove('is-active');
          item.setAttribute('aria-pressed', 'false');
        });
        button.classList.add('is-active');
        button.setAttribute('aria-pressed', 'true');
        const output = lab.querySelector('[data-sh-output]');
        if (output) {
          output.classList.remove('is-changing');
          void output.offsetWidth;
          output.textContent = button.dataset.shPrompt || button.textContent.trim();
          output.classList.add('is-changing');
        }
      });
    });
  };

  const initReimagineLabs = (root) => {
    findAll(root, '[data-sh-lab]').forEach((lab) => {
      if (lab.dataset.sourceReady === 'true') return;
      lab.dataset.sourceReady = 'true';
      const sourceLabel = lab.querySelector('[data-sh-lab-source]');
      const primary = lab.querySelector('[data-sh-lab-primary]');
      if (!sourceLabel || !primary) return;
      const defaultLabel = sourceLabel.textContent.trim();
      const updateSource = (selection) => {
        if (!selection?.id) {
          sourceLabel.textContent = defaultLabel;
          return;
        }
        const title = selection.title || 'StillHigh source';
        if (selection.reimagineReady) {
          sourceLabel.textContent = `YOUR SOURCE: ${title}`;
          primary.href = selection.url;
          primary.target = '_blank';
          primary.rel = 'noopener';
          primary.textContent = 'OPEN THIS SOURCE ON YOUTUBE ↗';
          primary.setAttribute('aria-label', `Open ${title} on YouTube`);
          primary.removeAttribute('data-sh-tv-jump');
          primary.removeAttribute('data-sh-tv-lead');
        } else {
          sourceLabel.textContent = `SELECTED: ${title} // CHOOSE A REMIX READY SOURCE`;
          primary.href = '#sh-tv-console-titan_youtube';
          primary.removeAttribute('target');
          primary.removeAttribute('rel');
          primary.textContent = 'CHOOSE A REMIX READY SOURCE ↑';
          primary.setAttribute('aria-label', 'Tune to a Remix Ready StillHigh source');
          primary.dataset.shTvJump = 'movement';
          primary.dataset.shTvLead = '_AMSjethpbY';
        }
      };
      const handleSelection = (event) => updateSource(event.detail);
      window.addEventListener('stillhigh:channelchange', handleSelection);
      try { updateSource(JSON.parse(sessionStorage.getItem('stillhigh-tv-selection') || 'null')); } catch (_) {}
      lab._shCleanup = () => window.removeEventListener('stillhigh:channelchange', handleSelection);
    });
  };

  const initInteractiveTV = (root) => {
    findAll(root, '[data-sh-tv]').forEach((tv) => {
      if (tv.dataset.tvReady === 'true') return;
      tv.dataset.tvReady = 'true';
      let cards = [...tv.querySelectorAll('[data-sh-tv-card]')];
      const guide = tv.querySelector('[data-sh-tv-guide]');
      const catalogSource = window.STILLHIGH_CHANNEL_CATALOG || {};
      const catalogItems = Array.isArray(catalogSource.items) ? catalogSource.items.map((row) => ({
        id: row[0],
        title: row[1],
        primaryCategory: row[2] || 'worlds',
        categories: String(row[3] || row[2] || 'worlds').split(/\s+/).filter(Boolean),
        format: row[4] || 'short',
        duration: row[5] || '',
        fullPosition: Number(row[6]) || 0,
        retroPosition: Number(row[7]) || 0
      })) : [];
      const catalogById = new Map(catalogItems.map((item) => [item.id, item]));
      const consoleEl = tv.querySelector('[data-sh-tv-console]');
      const screen = tv.querySelector('[data-sh-tv-screen]');
      let frame = tv.querySelector('[data-sh-tv-frame]');
      const cover = tv.querySelector('[data-sh-tv-cover-image]');
      const title = tv.querySelector('[data-sh-tv-title]');
      const description = tv.querySelector('[data-sh-tv-description]');
      const category = tv.querySelector('[data-sh-tv-category]');
      const format = tv.querySelector('[data-sh-tv-format]');
      const channel = tv.querySelector('[data-sh-tv-channel]');
      const youtubeLink = tv.querySelector('[data-sh-tv-youtube]');
      const reimagineLink = tv.querySelector('[data-sh-tv-reimagine]');
      const saveButton = tv.querySelector('[data-sh-tv-save]');
      const queuePlayButton = tv.querySelector('[data-sh-tv-play-queue]');
      const toast = tv.querySelector('[data-sh-tv-toast]');
      const queueCount = tv.querySelector('[data-sh-tv-queue-count]');
      const visibleCount = tv.querySelector('[data-sh-tv-visible-count]');
      const guideLabel = tv.querySelector('[data-sh-tv-guide-label]');
      const moodStatus = tv.querySelector('[data-sh-tv-mood-status]');
      const miniBar = tv.querySelector('[data-sh-tv-mini-bar]');
      const miniTitle = tv.querySelector('[data-sh-tv-mini-title]');
      const miniChannel = tv.querySelector('[data-sh-tv-mini-channel]');
      const guideToggle = tv.querySelector('[data-sh-tv-guide-toggle]');
      const guideToggleLabel = tv.querySelector('[data-sh-tv-guide-toggle-label]');
      const allFilterLabel = tv.querySelector('[data-sh-tv-all-label]');
      const totalCount = tv.querySelector('[data-sh-tv-total-count]');
      const totalCopy = tv.querySelector('[data-sh-tv-total-copy]');
      const loadMoreButton = tv.querySelector('[data-sh-tv-load-more]');
      const loadMoreLabel = tv.querySelector('[data-sh-tv-load-more-label]');
      const loadMoreCount = tv.querySelector('[data-sh-tv-load-more-count]');
      const emptyState = tv.querySelector('[data-sh-tv-empty]');
      const shuffleButton = tv.querySelector('[data-sh-tv-shuffle]');
      const shuffleLabel = tv.querySelector('[data-sh-tv-shuffle-label]');
      const shuffleCount = tv.querySelector('[data-sh-tv-shuffle-count]');
      const shuffleAction = tv.querySelector('[data-sh-tv-shuffle-action]');
      const shuffleStatus = tv.querySelector('[data-sh-tv-shuffle-status]');
      const liveLabel = tv.querySelector('[data-sh-tv-live-label]');
      if (!cards.length || !consoleEl || !screen || !frame) return;
      const playerFrameTemplate = frame.cloneNode(false);
      playerFrameTemplate.removeAttribute('src');
      playerFrameTemplate.hidden = true;
      const rootElement = document.documentElement;
      const initialRootPalette = {
        accent: rootElement.style.getPropertyValue('--sh-live-accent'),
        accentTwo: rootElement.style.getPropertyValue('--sh-live-accent-2'),
        glow: rootElement.style.getPropertyValue('--sh-live-glow'),
        signal: rootElement.dataset.shSignal,
        mood: rootElement.dataset.shMood
      };

      cards.forEach((card, index) => {
        card.dataset.shBasePosition = String(index + 1);
        const item = catalogById.get(card.dataset.shVideoId);
        if (!item) {
          card.dataset.shCategories = card.dataset.shCategory || 'worlds';
          return;
        }
        const categories = new Set([...(item.categories || []), card.dataset.shCategory].filter(Boolean));
        card.dataset.shCategories = [...categories].join(' ');
        card.dataset.shFullPosition = String(item.fullPosition || 0);
        card.dataset.shRetroPosition = String(item.retroPosition || 0);
      });

      let saved = [];
      try { saved = JSON.parse(localStorage.getItem('stillhigh-tv-queue') || '[]'); } catch (_) { saved = []; }
      if (!Array.isArray(saved)) saved = [];
      const knownIds = new Set([...cards.map((card) => card.dataset.shVideoId), ...catalogItems.map((item) => item.id)]);
      const shuffleAvailable = Boolean(catalogItems.length && knownIds.size);
      saved = [...new Set(saved)].filter((id) => knownIds.has(id));
      if (totalCount) totalCount.textContent = String(knownIds.size);
      if (totalCopy) totalCopy.textContent = String(knownIds.size);
      if (shuffleCount) shuffleCount.textContent = knownIds.size.toLocaleString();
      const filterIds = new Map(['featured', 'community', 'safari', 'movement', 'retro', 'chaos', 'show', 'worlds', 'milestone'].map((key) => [key, new Set()]));
      catalogItems.forEach((item) => item.categories.forEach((key) => filterIds.get(key)?.add(item.id)));
      cards.forEach((card) => {
        const id = card.dataset.shVideoId;
        String(card.dataset.shCategories || card.dataset.shCategory || '').split(/\s+/).filter(Boolean).forEach((key) => filterIds.get(key)?.add(id));
        if (card.dataset.shFeatured === 'true') filterIds.get('featured')?.add(id);
      });
      tv.querySelectorAll('[data-sh-tv-filter-count]').forEach((badge) => {
        badge.textContent = String(filterIds.get(badge.dataset.shTvFilterCount)?.size || 0);
      });
      let current = cards[0];
      let activeFilter = 'all';
      let archiveExpanded = false;
      let toastTimer;
      let tuningTimer;
      let consoleAbove = false;
      let dockDismissed = false;
      let syncDock = () => {};
      let wireCard = null;
      let shuffleActive = false;
      let shuffleDeck = [];
      let shuffleCyclePosition = 0;
      let shuffleHistory = [];
      let shuffleHistoryIndex = -1;
      let consecutiveShuffleErrors = 0;
      let shuffleEpoch = 0;
      let shuffleAdvanceTimer = null;
      let shuffleMagicTimer = null;
      let shuffleArrivalTimer = null;
      let shuffleErrorTimer = null;
      let shufflePlaybackStartTimer = null;
      let suppressEndedUntilPlaying = false;
      let transientShuffleCard = null;
      let youtubePlayer = null;
      let youtubePlayerReady = false;
      let youtubePlayerPromise = null;
      let youtubeAttachEpoch = 0;
      let youtubeReadyTimer = null;
      let pendingPlayerVideoId = null;
      let pendingPlayerStop = false;
      let activePlayingVideoId = '';
      let playerTuneEpoch = 0;
      let playerPlayingTuneEpoch = 0;
      let stopShuffleMode = () => {};
      let ensureYouTubePlayer = () => Promise.resolve(null);
      let advanceShuffle = () => {};
      let previousShuffle = () => {};
      let handleShuffleFailure = () => {};
      const catalogChunkSize = 36;
      const shuffleHistoryLimit = 64;
      const materializedIds = new Set(cards.map((card) => card.dataset.shVideoId));
      const categoryLabels = {
        community: 'COMMUNITY SPOTLIGHT',
        safari: 'STILLHIGH SAFARI',
        movement: 'REIMAGINE IMPOSSIBLE',
        retro: 'QUICKHITS RETRO',
        chaos: 'AI CHAOS',
        show: 'STILLHIGH SHOWS',
        worlds: 'STILLHIGH WORLDS',
        milestone: 'MILESTONES'
      };

      const positionFor = (card, key = activeFilter) => {
        const base = Number(card.dataset.shBasePosition) || 999999;
        const full = Number(card.dataset.shFullPosition) || 0;
        const retro = Number(card.dataset.shRetroPosition) || 0;
        if (key === 'saved') {
          const savedPosition = saved.indexOf(card.dataset.shVideoId);
          return savedPosition >= 0 ? savedPosition : base;
        }
        if (key === 'featured' || key === 'community' || (key === 'all' && !archiveExpanded)) return base;
        if (key === 'retro') return retro || (full ? 100000 + full : 900000 + base);
        return full || 900000 + base;
      };
      const channelLabelFor = (card) => {
        const full = Number(card?.dataset.shFullPosition) || 0;
        const retro = Number(card?.dataset.shRetroPosition) || 0;
        const base = Number(card?.dataset.shBasePosition) || Math.max(1, cards.indexOf(card) + 1);
        if (full) return `CH ${String(full).padStart(4, '0')}`;
        if (retro) return `RT ${String(retro).padStart(3, '0')}`;
        return `CH ${String(base).padStart(3, '0')}`;
      };
      cards.forEach((card) => {
        const badge = card.querySelector('.sh-tv-card__channel');
        if (badge) badge.textContent = channelLabelFor(card);
      });

      const createCatalogCard = (item) => {
        const card = document.createElement('button');
        const playlistId = item.fullPosition ? catalogSource.fullPlaylistId : catalogSource.retroPlaylistId;
        const playlistPosition = item.fullPosition || item.retroPosition;
        card.type = 'button';
        card.className = 'sh-tv-card sh-tv-card--standard is-full-catalog';
        card.dataset.shTvCard = '';
        card.dataset.shEssential = 'false';
        card.dataset.shVideoId = item.id;
        card.dataset.shVideoUrl = 'https://www.youtube.com/watch?v=' + encodeURIComponent(item.id) + '&list=' + encodeURIComponent(playlistId || '');
        card.dataset.shVideoTitle = item.title;
        card.dataset.shVideoDescription = 'Full Channel playlist entry ' + playlistPosition + (item.duration ? ' • ' + item.duration : '') + '.';
        card.dataset.shVideoCategory = categoryLabels[item.primaryCategory] || 'STILLHIGH ORIGINAL';
        card.dataset.shVideoFormat = item.format;
        card.dataset.shReimagine = 'false';
        card.dataset.shCategory = item.primaryCategory;
        card.dataset.shCategories = item.categories.join(' ');
        card.dataset.shFeatured = 'false';
        card.dataset.shFullPosition = String(item.fullPosition || 0);
        card.dataset.shRetroPosition = String(item.retroPosition || 0);
        card.dataset.shBasePosition = String(100000 + cards.length);
        card.dataset.shVideoThumbnail = 'https://i.ytimg.com/vi/' + encodeURIComponent(item.id) + '/maxresdefault.jpg';
        card.setAttribute('aria-label', 'Tune to ' + item.title);
        card.setAttribute('aria-pressed', 'false');
        card.hidden = true;

        const media = document.createElement('span');
        media.className = 'sh-tv-card__media';
        const image = document.createElement('img');
        image.src = card.dataset.shVideoThumbnail;
        image.dataset.shThumbFallback = 'https://i.ytimg.com/vi/' + encodeURIComponent(item.id) + '/hqdefault.jpg';
        image.alt = '';
        image.width = 1280;
        image.height = 720;
        image.loading = 'lazy';
        image.fetchPriority = 'low';
        image.decoding = 'async';
        media.append(image);

        const wash = document.createElement('span');
        wash.className = 'sh-tv-card__wash';
        const channelBadge = document.createElement('span');
        channelBadge.className = 'sh-tv-card__channel';
        channelBadge.textContent = channelLabelFor(card);
        const rank = document.createElement('span');
        rank.className = 'sh-tv-card__rank';
        rank.textContent = item.retroPosition ? 'RETRO ' + String(item.retroPosition).padStart(3, '0') : 'FULL ' + String(item.fullPosition).padStart(4, '0');
        const play = document.createElement('span');
        play.className = 'sh-tv-card__play';
        play.setAttribute('aria-hidden', 'true');
        play.append(document.createElement('i'));
        const copy = document.createElement('span');
        copy.className = 'sh-tv-card__copy';
        const small = document.createElement('small');
        small.textContent = categoryLabels[item.primaryCategory] || 'STILLHIGH ORIGINAL';
        const strong = document.createElement('strong');
        strong.textContent = item.title;
        const tune = document.createElement('em');
        tune.textContent = 'TUNE IN →';
        copy.append(small, strong, tune);
        card.append(media, wash, channelBadge, rank, play, copy);
        return card;
      };

      const catalogCandidates = (key) => catalogItems
        .filter((item) => key === 'all'
          || (key === 'saved' && saved.includes(item.id))
          || item.categories.includes(key))
        .sort((a, b) => {
          if (key === 'retro') return (a.retroPosition || 100000 + a.fullPosition) - (b.retroPosition || 100000 + b.fullPosition);
          return (a.fullPosition || 900000 + a.retroPosition) - (b.fullPosition || 900000 + b.retroPosition);
        });
      const appendCatalogItem = (item, destination = guide) => {
        if (!item || materializedIds.has(item.id)) return cards.find((card) => card.dataset.shVideoId === item?.id) || null;
        const card = createCatalogCard(item);
        materializedIds.add(item.id);
        cards.push(card);
        destination?.append(card);
        wireCard?.(card);
        return card;
      };
      const pruneTransientShuffleCard = (options = {}) => {
        if (!transientShuffleCard) return;
        const restoreFocus = options.restoreFocus !== false && transientShuffleCard.contains(document.activeElement);
        const transientId = transientShuffleCard.dataset.shVideoId;
        transientShuffleCard.remove();
        cards = cards.filter((card) => card !== transientShuffleCard);
        materializedIds.delete(transientId);
        transientShuffleCard = null;
        if (restoreFocus && tv.isConnected) {
          try { (shuffleButton || consoleEl).focus({ preventScroll: true }); }
          catch (_) { (shuffleButton || consoleEl).focus(); }
        }
      };
      const cardForShuffleId = (id) => {
        const existing = cards.find((card) => card.dataset.shVideoId === id);
        if (existing) {
          if (transientShuffleCard && transientShuffleCard !== existing) pruneTransientShuffleCard();
          return existing;
        }
        const item = catalogById.get(id);
        if (!item || !guide) return null;
        pruneTransientShuffleCard();
        const card = createCatalogCard(item);
        card.dataset.shLeanbackTransient = 'true';
        card.hidden = false;
        materializedIds.add(id);
        cards.push(card);
        guide.append(card);
        wireCard?.(card);
        transientShuffleCard = card;
        return card;
      };
      const updateLoadMore = (key = activeFilter) => {
        if (!loadMoreButton) return;
        const canExpand = Boolean(catalogItems.length)
          && key !== 'featured'
          && (key !== 'all' || archiveExpanded);
        const remaining = canExpand
          ? catalogCandidates(key).filter((item) => !materializedIds.has(item.id)).length
          : 0;
        loadMoreButton.hidden = remaining === 0;
        if (!remaining) return;
        const nextCount = Math.min(catalogChunkSize, remaining);
        if (visibleCount) visibleCount.textContent = `${visibleCards().length} LOADED · ${remaining} MORE`;
        if (loadMoreLabel) loadMoreLabel.textContent = `Load ${nextCount} more transmissions`;
        if (loadMoreCount) loadMoreCount.textContent = `+${nextCount}`;
        loadMoreButton.setAttribute('aria-label', `Load ${nextCount} more transmissions. ${remaining} remain in this channel.`);
      };
      const materializeCatalog = (key, options = {}) => {
        if (!guide || !catalogItems.length || key === 'featured') return { added: 0, ensured: null };
        const candidates = catalogCandidates(key);
        const fragment = document.createDocumentFragment();
        let ensured = null;
        let added = 0;
        if (options.ensureId) {
          const existing = cards.find((card) => card.dataset.shVideoId === options.ensureId);
          ensured = existing || appendCatalogItem(catalogById.get(options.ensureId), fragment);
          if (!existing && ensured) added += 1;
        }
        const requestedLimit = options.limit === undefined ? catalogChunkSize : Number(options.limit);
        const limit = requestedLimit === Number.POSITIVE_INFINITY ? Number.POSITIVE_INFINITY : Math.max(0, requestedLimit || 0);
        for (const item of candidates) {
          if (materializedIds.has(item.id)) continue;
          if (added >= limit) break;
          if (appendCatalogItem(item, fragment)) added += 1;
        }
        guide.append(fragment);
        return { added, ensured };
      };

      const say = (message) => {
        if (!toast) return;
        toast.textContent = message;
        window.clearTimeout(toastTimer);
        toastTimer = window.setTimeout(() => { toast.textContent = ''; }, 2600);
      };
      const persistSaved = () => {
        try { localStorage.setItem('stillhigh-tv-queue', JSON.stringify(saved)); } catch (_) {}
      };
      persistSaved();
      const isSaved = (card) => saved.includes(card?.dataset.shVideoId);
      const refreshSaved = () => {
        cards.forEach((card) => card.classList.toggle('is-saved', isSaved(card)));
        if (queueCount) queueCount.textContent = String(saved.length);
        if (saveButton) {
          const selected = isSaved(current);
          saveButton.disabled = !current;
          saveButton.classList.toggle('is-saved', selected);
          saveButton.setAttribute('aria-pressed', selected ? 'true' : 'false');
          saveButton.innerHTML = selected ? '<span aria-hidden="true">&#10003;</span> Saved to queue' : '<span aria-hidden="true">+</span> Save to queue';
        }
        if (queuePlayButton) {
          queuePlayButton.disabled = saved.length === 0;
          queuePlayButton.textContent = saved.length ? `Play queue (${saved.length})` : 'Play queue';
        }
      };
      const setSelectionAvailable = (available) => {
        tv.classList.toggle('is-empty', !available);
        screen.classList.toggle('is-empty', !available);
        tv.querySelectorAll('[data-sh-tv-play],[data-sh-tv-prev],[data-sh-tv-next],[data-sh-tv-copy]').forEach((button) => {
          button.disabled = !available;
        });
        [youtubeLink, reimagineLink].forEach((link) => {
          if (!link) return;
          if (available) {
            link.removeAttribute('aria-disabled');
            link.removeAttribute('tabindex');
          } else {
            link.setAttribute('aria-disabled', 'true');
            link.setAttribute('tabindex', '-1');
          }
        });
      };
      const visibleCards = () => {
        const visible = cards.filter((card) => !card.hidden).sort((a, b) => positionFor(a) - positionFor(b));
        if (activeFilter !== 'saved') return visible;
        return saved.map((id) => visible.find((card) => card.dataset.shVideoId === id)).filter(Boolean);
      };
      const refreshCount = () => {
        const count = visibleCards().length;
        if (visibleCount) visibleCount.textContent = `${count} TRANSMISSION${count === 1 ? '' : 'S'}`;
      };
      const activePlayerFrame = () => {
        try { return youtubePlayer?.getIframe?.() || frame; }
        catch (_) { return frame; }
      };
      const playerVideoId = (player) => {
        try { return player?.getVideoData?.()?.video_id || ''; }
        catch (_) { return ''; }
      };
      const resetInitializingYouTubePlayer = () => {
        youtubeAttachEpoch += 1;
        window.clearTimeout(youtubeReadyTimer);
        youtubeReadyTimer = null;
        const liveFrame = activePlayerFrame();
        const hadPlayerObject = Boolean(youtubePlayer);
        try { youtubePlayer?.destroy?.(); } catch (_) {}
        if (hadPlayerObject) {
          try { liveFrame?.remove?.(); } catch (_) {}
          const replacement = playerFrameTemplate.cloneNode(false);
          replacement.hidden = true;
          if (cover?.parentNode === screen) screen.insertBefore(replacement, cover);
          else screen.prepend(replacement);
          frame = replacement;
        } else {
          frame.removeAttribute('src');
          frame.hidden = true;
        }
        youtubePlayer = null;
        youtubePlayerReady = false;
        youtubePlayerPromise = null;
        pendingPlayerVideoId = null;
        pendingPlayerStop = true;
        activePlayingVideoId = '';
        playerPlayingTuneEpoch = 0;
      };
      const stopPlayback = () => {
        pendingPlayerVideoId = null;
        pendingPlayerStop = true;
        activePlayingVideoId = '';
        suppressEndedUntilPlaying = true;
        window.clearTimeout(shuffleErrorTimer);
        shuffleErrorTimer = null;
        window.clearTimeout(shufflePlaybackStartTimer);
        shufflePlaybackStartTimer = null;
        if (!youtubePlayerReady && (youtubePlayer || youtubePlayerPromise)) resetInitializingYouTubePlayer();
        if (youtubePlayerReady) {
          try { youtubePlayer.pauseVideo(); } catch (_) {}
        } else if (!youtubePlayer) {
          frame.removeAttribute('src');
        }
        activePlayerFrame().hidden = true;
        screen.classList.remove('is-playing', 'is-docked');
        window.dispatchEvent(new CustomEvent('stillhigh:playbackchange', { detail: { playing: false } }));
        tv.classList.remove('is-player-docked');
        if (miniBar) miniBar.hidden = true;
      };
      const thumbFor = (card) => card.dataset.shVideoThumbnail || `https://i.ytimg.com/vi/${encodeURIComponent(card.dataset.shVideoId)}/maxresdefault.jpg`;
      const palettes = {
        community: ['#e8ff4f', '#ff4fd8', 'rgba(232,255,79,.22)'],
        safari: ['#b8ff63', '#49e7ff', 'rgba(184,255,99,.24)'],
        movement: ['#49e7ff', '#a855f7', 'rgba(73,231,255,.24)'],
        retro: ['#ffb38a', '#67d9ff', 'rgba(255,179,138,.24)'],
        chaos: ['#ff4fd8', '#e8ff4f', 'rgba(255,79,216,.25)'],
        show: ['#67d9ff', '#a855f7', 'rgba(103,217,255,.22)'],
        hits: ['#ffe2a8', '#ff4fd8', 'rgba(255,226,168,.22)'],
        worlds: ['#70ffd4', '#9d79ff', 'rgba(112,255,212,.22)'],
        milestone: ['#e8ff4f', '#ffffff', 'rgba(232,255,79,.2)']
      };
      const paintPalette = (card) => {
        const palette = palettes[card?.dataset.shCategory] || palettes.worlds;
        tv.style.setProperty('--sh-tv-accent', palette[0]);
        tv.style.setProperty('--sh-tv-accent-2', palette[1]);
        tv.style.setProperty('--sh-tv-glow', palette[2]);
        document.documentElement.style.setProperty('--sh-live-accent', palette[0]);
        document.documentElement.style.setProperty('--sh-live-accent-2', palette[1]);
        document.documentElement.style.setProperty('--sh-live-glow', palette[2]);
        document.documentElement.dataset.shSignal = card?.dataset.shCategory || 'worlds';
      };
      const setMoodStatus = (label) => {
        if (!moodStatus) return;
        const dot = moodStatus.querySelector('i');
        moodStatus.replaceChildren();
        if (dot) moodStatus.append(dot);
        moodStatus.append(` ${label}`);
      };
      const clearFrequencyState = () => {
        tv.querySelectorAll('[data-sh-tv-frequency]').forEach((button) => button.setAttribute('aria-pressed', 'false'));
        tv.removeAttribute('data-sh-mood');
        document.documentElement.removeAttribute('data-sh-mood');
        setMoodStatus('SIGNAL OPEN');
        window.dispatchEvent(new CustomEvent('stillhigh:moodchange', { detail: { key: null, label: 'SIGNAL OPEN' } }));
      };
      const paintArchiveState = () => {
        guideToggle?.setAttribute('aria-expanded', archiveExpanded ? 'true' : 'false');
        if (guideToggleLabel) guideToggleLabel.textContent = archiveExpanded ? 'Show starting transmissions' : 'Open archive guide';
        if (allFilterLabel) allFilterLabel.textContent = archiveExpanded ? 'All transmissions' : 'Start here';
      };
      const selectCard = (card, options = {}) => {
        if (!card) return;
        if (shuffleActive && !options.shuffle) stopShuffleMode({ keepPlayback: true, announce: false });
        stopPlayback();
        current = card;
        if (transientShuffleCard && transientShuffleCard !== current) pruneTransientShuffleCard();
        setSelectionAvailable(true);
        if (emptyState) emptyState.hidden = true;
        paintPalette(card);
        if (!reduceMotion) {
          window.clearTimeout(tuningTimer);
          screen.classList.remove('is-tuning');
          void screen.offsetWidth;
          screen.classList.add('is-tuning');
          tuningTimer = window.setTimeout(() => screen.classList.remove('is-tuning'), 380);
        }
        cards.forEach((item) => {
          const selected = item === card;
          item.classList.toggle('is-active', selected);
          item.setAttribute('aria-pressed', selected ? 'true' : 'false');
        });
        const id = card.dataset.shVideoId;
        const isShort = card.dataset.shVideoFormat === 'short';
        screen.classList.toggle('is-short', isShort);
        if (cover) {
          cover.src = thumbFor(card);
          cover.dataset.fallbackSrc = `https://i.ytimg.com/vi/${encodeURIComponent(id)}/hqdefault.jpg`;
          cover.alt = card.dataset.shVideoTitle || 'StillHigh transmission';
        }
        tv.querySelectorAll('[data-sh-tv-play]').forEach((button) => {
          button.setAttribute('aria-label', `Play ${card.dataset.shVideoTitle || 'selected channel'}`);
        });
        const officialTitle = card.dataset.shVideoTitle || 'StillHigh';
        const displayTitle = card.dataset.shDisplayTitle || officialTitle.replace(/\s+#\S.*$/u, '').trim() || officialTitle;
        if (title) {
          title.textContent = displayTitle;
          title.title = officialTitle;
          title.classList.toggle('is-long-title', displayTitle.length > 42);
        }
        if (description) description.textContent = card.dataset.shVideoDescription || '';
        if (category) category.textContent = card.dataset.shVideoCategory || 'STILLHIGH ORIGINAL';
        if (format) format.textContent = isShort ? 'SHORT' : 'FEATURE';
        const channelLabel = channelLabelFor(card);
        if (channel) channel.textContent = channelLabel;
        if (miniTitle) miniTitle.textContent = displayTitle;
        if (miniChannel) miniChannel.textContent = channelLabel;
        frame.title = `${displayTitle} — StillHigh Interactive TV player`;
        const url = card.dataset.shVideoUrl || `https://www.youtube.com/watch?v=${encodeURIComponent(id)}`;
        if (youtubeLink) youtubeLink.href = url;
        const canReimagine = card.dataset.shReimagine === 'true';
        if (reimagineLink) {
          reimagineLink.href = url;
          reimagineLink.hidden = !canReimagine;
        }
        refreshSaved();
        const selection = {
          id,
          title: displayTitle,
          officialTitle,
          url,
          thumbnail: thumbFor(card),
          category: card.dataset.shCategory || 'worlds',
          reimagineReady: canReimagine
        };
        try {
          sessionStorage.setItem('stillhigh-tv-last', id);
          sessionStorage.setItem('stillhigh-tv-selection', JSON.stringify(selection));
        } catch (_) {}
        if (tv.dataset.shHistoryReady === 'true' && options.history !== false) {
          const link = new URL(window.location.href);
          if (link.searchParams.get('transmission') !== id) {
            link.searchParams.set('transmission', id);
            history.pushState(null, '', link);
          }
        }
        window.dispatchEvent(new CustomEvent('stillhigh:channelchange', { detail: selection }));
        if (options.play) playCurrent();
        if (options.scroll) consoleEl.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      };
      wireCard = (card) => {
        if (!card || card.dataset.shCardReady === 'true') return;
        card.dataset.shCardReady = 'true';
        const image = card.querySelector('img[data-sh-thumb-fallback]');
        image?.addEventListener('error', () => {
          const fallback = image.dataset.shThumbFallback;
          if (fallback && image.src !== fallback) image.src = fallback;
        });
        card.addEventListener('click', () => {
          clearFrequencyState();
          selectCard(card, { play: true, scroll: true });
        });
      };
      const playCurrent = () => {
        if (!current) return;
        const id = current.dataset.shVideoId;
        const origin = encodeURIComponent(window.location.origin);
        const referrer = encodeURIComponent(window.location.href.split('#')[0]);
        playerTuneEpoch += 1;
        playerPlayingTuneEpoch = 0;
        window.clearTimeout(shuffleErrorTimer);
        shuffleErrorTimer = null;
        pendingPlayerVideoId = id;
        pendingPlayerStop = false;
        activePlayingVideoId = '';
        suppressEndedUntilPlaying = true;
        if (youtubePlayerReady) {
          try { youtubePlayer.loadVideoById(id); } catch (_) {}
        } else if (!youtubePlayer) {
          frame.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0&playsinline=1&enablejsapi=1&origin=${origin}&widget_referrer=${referrer}`;
        }
        activePlayerFrame().hidden = false;
        screen.classList.add('is-playing');
        window.dispatchEvent(new CustomEvent('stillhigh:playbackchange', { detail: { playing: true } }));
        dockDismissed = false;
        syncDock();
        if (shuffleActive) {
          ensureYouTubePlayer();
          const expectedTuneEpoch = playerTuneEpoch;
          const expectedVideoId = id;
          window.clearTimeout(shufflePlaybackStartTimer);
          shufflePlaybackStartTimer = window.setTimeout(() => {
            if (
              !shuffleActive
              || expectedTuneEpoch !== playerTuneEpoch
              || current?.dataset.shVideoId !== expectedVideoId
              || playerPlayingTuneEpoch === expectedTuneEpoch
            ) return;
            handleShuffleFailure('That transmission did not start in time. Retuning the StillHigh signal…');
          }, 12000);
        }
      };
      const move = (direction, play = true) => {
        let choices = visibleCards();
        if (!choices.length) return;
        let index = choices.indexOf(current);
        if (index < 0) index = 0;
        if (direction > 0 && index === choices.length - 1 && loadMoreButton && !loadMoreButton.hidden) {
          const { added } = materializeCatalog(activeFilter, { limit: catalogChunkSize });
          if (added) {
            applyFilter(activeFilter, { autoSelect: false, skipMaterialize: true });
            choices = visibleCards();
            index = Math.max(0, choices.indexOf(current));
          }
        }
        index = (index + direction + choices.length) % choices.length;
        selectCard(choices[index], { play });
      };
      const random = () => {
        const candidateIds = activeFilter === 'all'
          ? [...knownIds]
          : activeFilter === 'saved'
            ? [...saved]
            : [...(filterIds.get(activeFilter) || [])];
        if (!candidateIds.length) return;
        const currentId = current?.dataset.shVideoId;
        const pool = candidateIds.length > 1 ? candidateIds.filter((id) => id !== currentId) : candidateIds;
        const surpriseId = pool[Math.floor(Math.random() * pool.length)];
        let surprise = cards.find((card) => card.dataset.shVideoId === surpriseId);
        if (!surprise) surprise = materializeCatalog(activeFilter, { ensureId: surpriseId, limit: 0 }).ensured;
        if (!surprise) return;
        if (activeFilter === 'all' && !archiveExpanded && surprise.dataset.shEssential !== 'true') {
          archiveExpanded = true;
          paintArchiveState();
          say('Deep cut found. The full channel guide is now open.');
        }
        applyFilter(activeFilter, { clearFrequency: activeFilter === 'all', autoSelect: false, skipMaterialize: true });
        selectCard(surprise, { play: true, scroll: true });
      };
      const applyFilter = (key, options = {}) => {
        activeFilter = key;
        tv.classList.toggle('is-guide-expanded', archiveExpanded || key !== 'all');
        const guideLabels = {
          featured: 'Audience Favorites',
          community: 'Community Spotlight',
          safari: 'StillHigh Safari',
          movement: 'Reimagine Ideas',
          retro: 'QuickHits Retro',
          chaos: 'AI Chaos',
          show: 'Shows',
          worlds: 'Worlds',
          milestone: 'Milestones',
          saved: 'Saved Queue'
        };
        if (guideLabel) guideLabel.textContent = key === 'all' ? (archiveExpanded ? 'ALL STILLHIGH TRANSMISSIONS' : 'CHOOSE YOUR NEXT TRANSMISSION') : (guideLabels[key] || 'CHOOSE YOUR NEXT TRANSMISSION');
        if (options.clearFrequency) clearFrequencyState();
        if (!options.skipMaterialize) {
          if (key === 'all') {
            if (archiveExpanded) materializeCatalog('all');
          } else if (key === 'saved') {
            materializeCatalog('saved', { ensureId: saved[0], limit: catalogChunkSize });
          } else if (key !== 'featured') {
            materializeCatalog(key);
          }
        }
        tv.querySelectorAll('[data-sh-tv-filter]').forEach((button) => {
          const active = button.dataset.shTvFilter === key;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        cards.forEach((card) => {
          const categories = String(card.dataset.shCategories || card.dataset.shCategory || '').split(/\s+/).filter(Boolean);
          const categoryMatch = key === 'all'
            || (key === 'featured' && card.dataset.shFeatured === 'true')
            || (key === 'saved' && isSaved(card))
            || categories.includes(key);
          const show = categoryMatch && (archiveExpanded || key !== 'all' || card.dataset.shEssential === 'true');
          card.hidden = !show;
          card.style.order = String(positionFor(card, key));
        });
        if (guide) {
          const ordered = document.createDocumentFragment();
          [...cards].sort((a, b) => positionFor(a, key) - positionFor(b, key)).forEach((card) => ordered.append(card));
          if (emptyState) guide.insertBefore(ordered, emptyState);
          else guide.append(ordered);
        }
        refreshCount();
        updateLoadMore(key);
        const matches = visibleCards();
        if (emptyState) emptyState.hidden = !(key === 'saved' && !matches.length);
        if (!matches.length) {
          stopPlayback();
          cards.forEach((card) => {
            card.classList.remove('is-active');
            card.setAttribute('aria-pressed', 'false');
          });
          current = null;
          setSelectionAvailable(false);
          if (category) category.textContent = 'SAVED QUEUE';
          if (format) format.textContent = 'OFF AIR';
          if (channel) channel.textContent = '--';
          if (title) {
            title.textContent = 'Your queue is empty';
            title.title = 'Your queue is empty';
            title.classList.remove('is-long-title');
          }
          if (description) description.textContent = 'Save a transmission, then return here to play your own StillHigh channel.';
          if (miniTitle) miniTitle.textContent = 'Your queue is empty';
          if (miniChannel) miniChannel.textContent = '--';
          refreshSaved();
        } else if (options.autoSelect !== false && (!current || current.hidden)) selectCard(matches[0]);
        if (key === 'saved' && !matches.length) say('Your queue is empty. Save a channel with the + button.');
      };

      const requestedFilter = new URLSearchParams(window.location.search).get('titan_channel');
      if (requestedFilter && tv.querySelector(`[data-sh-tv-filter="${CSS.escape(requestedFilter)}"]`)) {
        archiveExpanded = requestedFilter !== 'all';
        paintArchiveState();
        window.setTimeout(() => applyFilter(requestedFilter), 0);
      }

      const tuneFrequency = (key, leadId, options = {}) => {
        applyFilter(key || 'all', { autoSelect: false });
        const choices = visibleCards();
        const lead = choices.find((card) => card.dataset.shVideoId === leadId) || choices[0];
        if (lead) selectCard(lead, { play: Boolean(options.play), scroll: options.scroll !== false });
        let activeMoodLabel = '';
        tv.querySelectorAll('[data-sh-tv-frequency]').forEach((button) => {
          const active = button.dataset.shTvFrequency === key
            && (!leadId || button.dataset.shTvLead === leadId);
          button.setAttribute('aria-pressed', active ? 'true' : 'false');
          if (active) activeMoodLabel = button.dataset.shMoodLabel || button.querySelector('strong')?.textContent || key;
        });
        if (key) {
          tv.dataset.shMood = key;
          document.documentElement.dataset.shMood = key;
          setMoodStatus(`${activeMoodLabel || key} LOCKED`);
          window.dispatchEvent(new CustomEvent('stillhigh:moodchange', { detail: { key, label: activeMoodLabel || key } }));
        }
      };

      const setShuffleStatus = (message, options = {}) => {
        if (!shuffleStatus) return;
        const announce = options.announce !== false;
        shuffleStatus.setAttribute('aria-live', announce ? 'polite' : 'off');
        shuffleStatus.textContent = message;
        if (!announce) {
          window.requestAnimationFrame(() => {
            if (shuffleStatus.isConnected) shuffleStatus.setAttribute('aria-live', 'polite');
          });
        }
      };
      const igniteShuffleMagic = () => {
        if (reduceMotion) return;
        window.clearTimeout(shuffleMagicTimer);
        tv.classList.remove('is-shuffle-igniting');
        void tv.offsetWidth;
        tv.classList.add('is-shuffle-igniting');
        shuffleMagicTimer = window.setTimeout(() => tv.classList.remove('is-shuffle-igniting'), 1500);
      };
      const renderShuffleState = () => {
        tv.classList.toggle('is-shuffle-live', shuffleActive);
        title?.setAttribute('aria-live', shuffleActive ? 'off' : 'polite');
        visibleCount?.setAttribute('aria-live', shuffleActive ? 'off' : 'polite');
        if (shuffleButton) {
          shuffleButton.disabled = !shuffleAvailable;
          shuffleButton.setAttribute('aria-disabled', shuffleAvailable ? 'false' : 'true');
        }
        shuffleButton?.setAttribute('aria-pressed', shuffleActive ? 'true' : 'false');
        shuffleButton?.setAttribute(
          'aria-label',
          !shuffleAvailable
            ? 'Click me — shuffle everything. StillHighTV is temporarily unavailable because the complete channel guide did not load.'
            : shuffleActive
            ? 'StillHighTV is live — click to stop. Stop StillHighTV Shuffle.'
            : 'Click me — shuffle everything. Start StillHighTV Shuffle. Plays the entire StillHigh library in random order. Hotkey Shift plus T.'
        );
        if (shuffleLabel) shuffleLabel.textContent = shuffleActive
          ? 'STILLHIGHTV IS LIVE — CLICK TO STOP'
          : 'CLICK ME — SHUFFLE EVERYTHING';
        if (shuffleAction) shuffleAction.textContent = shuffleActive ? 'STOP' : 'START';
        if (liveLabel) liveLabel.textContent = shuffleActive ? 'STILLHIGHTV SHUFFLE' : 'STILLHIGH LIVE';
        if (shuffleActive) document.documentElement.dataset.shTvShuffle = 'true';
        else delete document.documentElement.dataset.shTvShuffle;
        if (!shuffleAvailable) setShuffleStatus('The complete StillHigh channel guide did not load. Refresh the page to restore StillHighTV.');
      };
      const refillShuffleDeck = () => {
        const nextDeck = [...knownIds].filter(Boolean);
        for (let index = nextDeck.length - 1; index > 0; index -= 1) {
          const swapIndex = Math.floor(Math.random() * (index + 1));
          [nextDeck[index], nextDeck[swapIndex]] = [nextDeck[swapIndex], nextDeck[index]];
        }
        if (nextDeck.length > 1 && nextDeck[0] === current?.dataset.shVideoId) {
          [nextDeck[0], nextDeck[nextDeck.length - 1]] = [nextDeck[nextDeck.length - 1], nextDeck[0]];
        }
        shuffleDeck = nextDeck;
        shuffleCyclePosition = 0;
      };
      const scheduleShuffleAdvance = (reason, delay = 260) => {
        window.clearTimeout(shuffleAdvanceTimer);
        const expectedEpoch = shuffleEpoch;
        shuffleAdvanceTimer = window.setTimeout(() => {
          if (!shuffleActive || expectedEpoch !== shuffleEpoch || !tv.isConnected) return;
          advanceShuffle(reason);
        }, delay);
      };
      handleShuffleFailure = (message) => {
        if (!shuffleActive) return;
        window.clearTimeout(shufflePlaybackStartTimer);
        shufflePlaybackStartTimer = null;
        activePlayingVideoId = '';
        suppressEndedUntilPlaying = true;
        consecutiveShuffleErrors += 1;
        if (consecutiveShuffleErrors >= 8) {
          stopShuffleMode({ announce: false });
          setShuffleStatus('StillHighTV paused after eight unavailable transmissions. Click the button or press Shift plus T to try a fresh signal.');
          return;
        }
        setShuffleStatus(message || 'That transmission is unavailable here. Retuning the StillHigh signal…');
        scheduleShuffleAdvance('error', 240);
      };
      ensureYouTubePlayer = () => {
        if (youtubePlayerReady) return Promise.resolve(youtubePlayer);
        if (youtubePlayerPromise) return youtubePlayerPromise;
        const attachmentEpoch = ++youtubeAttachEpoch;
        youtubePlayerPromise = loadYouTubePlayerApi()
          .then((YT) => new Promise((resolve, reject) => {
            if (attachmentEpoch !== youtubeAttachEpoch || !tv.isConnected || !frame.id) {
              reject(new Error('StillHighTV player was removed'));
              return;
            }
            try {
              youtubePlayer = new YT.Player(frame.id, {
                events: {
                  onReady: (event) => {
                    if (attachmentEpoch !== youtubeAttachEpoch || !tv.isConnected) {
                      try { event.target.destroy(); } catch (_) {}
                      reject(new Error('Stale StillHighTV player'));
                      return;
                    }
                    window.clearTimeout(youtubeReadyTimer);
                    youtubeReadyTimer = null;
                    youtubePlayerReady = true;
                    const requestedId = pendingPlayerVideoId;
                    const loadedId = playerVideoId(event.target);
                    if (pendingPlayerStop || !requestedId) {
                      pendingPlayerStop = false;
                      activePlayingVideoId = '';
                      try { event.target.pauseVideo(); } catch (_) {}
                      resolve(event.target);
                      return;
                    }
                    pendingPlayerStop = false;
                    if (requestedId && requestedId !== loadedId) {
                      suppressEndedUntilPlaying = true;
                      event.target.loadVideoById(requestedId);
                    } else if (requestedId && shuffleActive) {
                      try {
                        if (event.target.getPlayerState() !== YT.PlayerState.PLAYING) event.target.playVideo();
                      } catch (_) {}
                    }
                    resolve(event.target);
                  },
                  onStateChange: (event) => {
                    if (attachmentEpoch !== youtubeAttachEpoch || !tv.isConnected) return;
                    if (event.data === YT.PlayerState.PLAYING) {
                      const expectedId = current?.dataset.shVideoId || '';
                      const eventVideoId = playerVideoId(event.target);
                      if (expectedId && eventVideoId && eventVideoId !== expectedId) return;
                      activePlayingVideoId = eventVideoId || expectedId;
                      playerPlayingTuneEpoch = playerTuneEpoch;
                      window.clearTimeout(shuffleErrorTimer);
                      shuffleErrorTimer = null;
                      window.clearTimeout(shufflePlaybackStartTimer);
                      shufflePlaybackStartTimer = null;
                      pendingPlayerStop = false;
                      suppressEndedUntilPlaying = false;
                      consecutiveShuffleErrors = 0;
                      if (shuffleActive) {
                        const position = Math.max(1, shuffleCyclePosition);
                        setShuffleStatus(`SHUFFLE LIVE · ${position.toLocaleString()} OF ${knownIds.size.toLocaleString()} · ${current?.dataset.shVideoTitle || 'STILLHIGH TRANSMISSION'}`);
                      }
                      return;
                    }
                    if (event.data !== YT.PlayerState.ENDED || !shuffleActive || suppressEndedUntilPlaying) return;
                    const expectedId = current?.dataset.shVideoId || '';
                    const eventVideoId = playerVideoId(event.target);
                    if (!expectedId || activePlayingVideoId !== expectedId || (eventVideoId && eventVideoId !== expectedId)) return;
                    activePlayingVideoId = '';
                    suppressEndedUntilPlaying = true;
                    scheduleShuffleAdvance('ended');
                  },
                  onError: (event) => {
                    if (attachmentEpoch !== youtubeAttachEpoch || !shuffleActive) return;
                    const expectedId = current?.dataset.shVideoId || '';
                    const eventVideoId = playerVideoId(event.target);
                    if (!eventVideoId) return;
                    if (expectedId && eventVideoId && eventVideoId !== expectedId) return;
                    const errorTuneEpoch = playerTuneEpoch;
                    window.clearTimeout(shuffleErrorTimer);
                    shuffleErrorTimer = window.setTimeout(() => {
                      if (
                        attachmentEpoch !== youtubeAttachEpoch
                        || !shuffleActive
                        || errorTuneEpoch !== playerTuneEpoch
                        || current?.dataset.shVideoId !== expectedId
                        || playerPlayingTuneEpoch === errorTuneEpoch
                      ) return;
                      handleShuffleFailure('That transmission is unavailable here. Retuning the StillHigh signal…');
                    }, 140);
                  },
                  onAutoplayBlocked: () => {
                    if (attachmentEpoch !== youtubeAttachEpoch || !shuffleActive) return;
                    window.clearTimeout(shufflePlaybackStartTimer);
                    shufflePlaybackStartTimer = null;
                    setShuffleStatus('Your browser paused autoplay. Press Play inside the screen once; StillHighTV will continue shuffling afterward.');
                  }
                }
              });
              if (!youtubePlayerReady && attachmentEpoch === youtubeAttachEpoch) {
                youtubeReadyTimer = window.setTimeout(() => {
                  if (attachmentEpoch !== youtubeAttachEpoch || youtubePlayerReady) return;
                  const wasActive = shuffleActive;
                  resetInitializingYouTubePlayer();
                  if (wasActive) {
                    stopShuffleMode({ announce: false });
                    setShuffleStatus('StillHighTV could not finish connecting to the player. Click the button or press Shift plus T to try again.');
                  }
                  reject(new Error('StillHighTV player readiness timeout'));
                }, 9000);
              }
            } catch (error) {
              reject(error);
            }
          }))
          .catch(() => {
            if (attachmentEpoch !== youtubeAttachEpoch) return null;
            const wasActive = shuffleActive;
            resetInitializingYouTubePlayer();
            if (wasActive) {
              stopShuffleMode({ announce: false });
              setShuffleStatus('Automatic retuning was blocked. Click the button or press Shift plus T to try StillHighTV again.');
            }
            return null;
          });
        return youtubePlayerPromise;
      };
      const tuneShuffledCard = (nextCard, reason, replay = false) => {
        if (!nextCard || !shuffleActive) return false;
        archiveExpanded = true;
        paintArchiveState();
        applyFilter('all', { autoSelect: false, skipMaterialize: true });
        selectCard(nextCard, { play: true, shuffle: true });
        if (reason === 'start') {
          window.clearTimeout(shuffleArrivalTimer);
          shuffleArrivalTimer = window.setTimeout(() => {
            if (shuffleActive && tv.isConnected) {
              consoleEl.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
            }
          }, reduceMotion ? 0 : 560);
        }
        if (replay) {
          setShuffleStatus(`REPLAYING · HISTORY ${(shuffleHistoryIndex + 1).toLocaleString()} OF ${shuffleHistory.length.toLocaleString()} · ${nextCard.dataset.shVideoTitle || 'STILLHIGH TRANSMISSION'}`, { announce: false });
        } else {
          setShuffleStatus(`TUNING · ${shuffleCyclePosition.toLocaleString()} OF ${knownIds.size.toLocaleString()} · ${nextCard.dataset.shVideoTitle || 'STILLHIGH TRANSMISSION'}`, { announce: false });
        }
        return true;
      };
      advanceShuffle = (reason = 'next') => {
        if (!shuffleActive || !knownIds.size) return;
        window.clearTimeout(shuffleAdvanceTimer);
        shuffleAdvanceTimer = null;
        if (reason !== 'random' && shuffleHistoryIndex < shuffleHistory.length - 1) {
          shuffleHistoryIndex += 1;
          const forwardCard = cardForShuffleId(shuffleHistory[shuffleHistoryIndex]);
          if (tuneShuffledCard(forwardCard, reason, true)) return;
          shuffleHistory.splice(shuffleHistoryIndex, 1);
          shuffleHistoryIndex -= 1;
        } else if (reason === 'random' && shuffleHistoryIndex < shuffleHistory.length - 1) {
          shuffleHistory = shuffleHistory.slice(0, shuffleHistoryIndex + 1);
        }
        if (!shuffleDeck.length) refillShuffleDeck();
        const nextId = shuffleDeck.shift();
        const nextCard = cardForShuffleId(nextId);
        if (!nextCard) {
          setShuffleStatus('A channel could not be tuned. Searching for the next signal…', { announce: false });
          scheduleShuffleAdvance('missing', 40);
          return;
        }
        shuffleCyclePosition += 1;
        shuffleHistory.push(nextId);
        if (shuffleHistory.length > shuffleHistoryLimit) shuffleHistory.shift();
        shuffleHistoryIndex = shuffleHistory.length - 1;
        tuneShuffledCard(nextCard, reason);
      };
      previousShuffle = () => {
        if (!shuffleActive) {
          move(-1);
          return;
        }
        window.clearTimeout(shuffleAdvanceTimer);
        shuffleAdvanceTimer = null;
        if (shuffleHistoryIndex <= 0) {
          setShuffleStatus('This is the first transmission in the current StillHighTV session.');
          say('No earlier StillHighTV transmission yet.');
          return;
        }
        shuffleHistoryIndex -= 1;
        const previousCard = cardForShuffleId(shuffleHistory[shuffleHistoryIndex]);
        if (!tuneShuffledCard(previousCard, 'previous', true)) {
          shuffleHistory.splice(shuffleHistoryIndex, 1);
          shuffleHistoryIndex = Math.min(shuffleHistoryIndex, shuffleHistory.length - 1);
          advanceShuffle('next');
        }
      };
      const startShuffleMode = () => {
        if (!shuffleAvailable) {
          setShuffleStatus('The complete StillHigh channel guide is not available yet. Refresh the page and try again.');
          return;
        }
        shuffleEpoch += 1;
        shuffleActive = true;
        shuffleDeck = [];
        shuffleCyclePosition = 0;
        shuffleHistory = [];
        shuffleHistoryIndex = -1;
        consecutiveShuffleErrors = 0;
        suppressEndedUntilPlaying = true;
        igniteShuffleMagic();
        clearFrequencyState();
        archiveExpanded = true;
        paintArchiveState();
        applyFilter('all', { autoSelect: false, skipMaterialize: true });
        renderShuffleState();
        setShuffleStatus(`OPENING THE FULL SIGNAL · ${knownIds.size.toLocaleString()} TRANSMISSIONS · NO REPEATS`, { announce: false });
        advanceShuffle('start');
      };
      stopShuffleMode = (options = {}) => {
        const wasActive = shuffleActive;
        shuffleActive = false;
        shuffleEpoch += 1;
        shuffleDeck = [];
        shuffleCyclePosition = 0;
        shuffleHistory = [];
        shuffleHistoryIndex = -1;
        consecutiveShuffleErrors = 0;
        window.clearTimeout(shuffleAdvanceTimer);
        shuffleAdvanceTimer = null;
        window.clearTimeout(shuffleErrorTimer);
        shuffleErrorTimer = null;
        window.clearTimeout(shufflePlaybackStartTimer);
        shufflePlaybackStartTimer = null;
        window.clearTimeout(shuffleArrivalTimer);
        shuffleArrivalTimer = null;
        window.clearTimeout(shuffleMagicTimer);
        shuffleMagicTimer = null;
        tv.classList.remove('is-shuffle-igniting');
        renderShuffleState();
        if (!options.keepPlayback) stopPlayback();
        if (wasActive) {
          setShuffleStatus(
            'StillHighTV is off. Click the button or press Shift plus T to shuffle the complete library again.',
            { announce: options.announce !== false }
          );
        }
      };
      const toggleShuffleMode = () => {
        if (shuffleActive) stopShuffleMode();
        else startShuffleMode();
      };
      const handleShuffleHotkey = (event) => {
        if (event.defaultPrevented || event.repeat || !event.shiftKey || event.altKey || event.ctrlKey || event.metaKey) return;
        const target = event.target;
        if (target?.closest?.('input,textarea,select,[contenteditable="true"],[role="textbox"]')) return;
        if (event.key.toLowerCase() !== 't') return;
        event.preventDefault();
        toggleShuffleMode();
      };

      const desktopDock = window.matchMedia('(min-width: 821px)');
      syncDock = () => {
        const shouldPersist = consoleAbove
          && screen.classList.contains('is-playing')
          && !dockDismissed
          && !window.Shopify?.designMode;
        const dockScreen = shouldPersist && desktopDock.matches;
        screen.classList.toggle('is-docked', dockScreen);
        tv.classList.toggle('is-player-docked', dockScreen);
        if (miniBar) miniBar.hidden = !(shouldPersist && !desktopDock.matches);
      };
      const consoleObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
        const entry = entries[0];
        consoleAbove = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
        syncDock();
      }, { threshold: .12 }) : null;
      consoleObserver?.observe(consoleEl);
      const handleViewport = () => syncDock();
      window.addEventListener('resize', handleViewport, { passive: true });
      let touchStart = null;
      let suppressClickUntil = 0;
      const handleTouchStart = (event) => {
        if (event.touches.length !== 1 || screen.classList.contains('is-playing')) { touchStart = null; return; }
        touchStart = { x: event.touches[0].clientX, y: event.touches[0].clientY };
      };
      const handleTouchEnd = (event) => {
        if (!touchStart || screen.classList.contains('is-playing')) { touchStart = null; return; }
        const touch = event.changedTouches[0];
        const dx = touch.clientX - touchStart.x;
        const dy = touch.clientY - touchStart.y;
        touchStart = null;
        if (Math.abs(dx) < 58 || Math.abs(dx) < Math.abs(dy) * 1.35) return;
        event.preventDefault();
        suppressClickUntil = Date.now() + 450;
        move(dx < 0 ? 1 : -1, false);
        say('Channel tuned. Press Play to start the transmission.');
      };
      const suppressSwipeClick = (event) => {
        if (Date.now() >= suppressClickUntil) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      };
      const handleTouchCancel = () => { touchStart = null; };
      screen.addEventListener('touchstart', handleTouchStart, { passive: true });
      screen.addEventListener('touchend', handleTouchEnd, { passive: false });
      screen.addEventListener('touchcancel', handleTouchCancel, { passive: true });
      screen.addEventListener('click', suppressSwipeClick, true);

      cover?.addEventListener('error', () => {
        const fallback = cover.dataset.fallbackSrc;
        if (fallback && cover.src !== fallback) cover.src = fallback;
      });
      cards.forEach(wireCard);
      tv.querySelectorAll('[data-sh-tv-play]').forEach((button) => button.addEventListener('click', playCurrent));
      tv.querySelector('[data-sh-tv-prev]')?.addEventListener('click', () => {
        if (shuffleActive) previousShuffle();
        else move(-1);
      });
      tv.querySelector('[data-sh-tv-next]')?.addEventListener('click', () => {
        if (shuffleActive) advanceShuffle('next');
        else move(1);
      });
      tv.querySelectorAll('[data-sh-tv-random]').forEach((button) => button.addEventListener('click', () => {
        if (shuffleActive) advanceShuffle('random');
        else random();
      }));
      [youtubeLink, reimagineLink].forEach((link) => link?.addEventListener('click', () => {
        if (shuffleActive) stopShuffleMode({ announce: false });
      }));
      if (shuffleButton && shuffleAvailable) {
        shuffleButton.addEventListener('click', toggleShuffleMode);
        document.addEventListener('keydown', handleShuffleHotkey);
      }
      tv.querySelectorAll('[data-sh-tv-filter]').forEach((button) => button.addEventListener('click', () => {
        stopShuffleMode({ keepPlayback: true, announce: false });
        applyFilter(button.dataset.shTvFilter, { clearFrequency: true });
      }));
      tv.querySelectorAll('[data-sh-tv-frequency]').forEach((button) => button.addEventListener('click', () => {
        stopShuffleMode({ keepPlayback: true, announce: false });
        tuneFrequency(button.dataset.shTvFrequency, button.dataset.shTvLead, { scroll: true });
      }));
      queuePlayButton?.addEventListener('click', () => {
        stopShuffleMode({ keepPlayback: true, announce: false });
        clearFrequencyState();
        applyFilter('saved', { autoSelect: false });
        const firstQueued = saved.map((id) => cards.find((card) => card.dataset.shVideoId === id)).find(Boolean);
        if (firstQueued) selectCard(firstQueued, { play: true, scroll: true });
      });
      guideToggle?.addEventListener('click', () => {
        stopShuffleMode({ keepPlayback: true, announce: false });
        archiveExpanded = !archiveExpanded;
        paintArchiveState();
        applyFilter('all', { clearFrequency: true, autoSelect: true });
        say(archiveExpanded ? 'Archive guide opened. Load more transmissions in fast batches.' : 'Showing the ' + Math.min(8, cards.length) + ' starting transmissions.');
      });
      loadMoreButton?.addEventListener('click', () => {
        const { added } = materializeCatalog(activeFilter, { limit: catalogChunkSize });
        applyFilter(activeFilter, { autoSelect: false, skipMaterialize: true });
        say(added ? `${added} more transmissions loaded.` : 'Every transmission in this channel is already loaded.');
      });
      saveButton?.addEventListener('click', () => {
        const id = current?.dataset.shVideoId;
        if (!id) return;
        saved = saved.includes(id) ? saved.filter((item) => item !== id) : [...saved, id];
        persistSaved();
        refreshSaved();
        if (activeFilter === 'saved') applyFilter('saved');
        say(saved.includes(id) ? 'Added to your queue.' : 'Removed from your queue.');
      });
      tv.querySelector('[data-sh-tv-copy]')?.addEventListener('click', async () => {
        const url = current ? (() => { const link = new URL(window.location.href); link.searchParams.set('transmission', current.dataset.shVideoId); link.hash = 'watch'; return link.href; })() : '';
        if (!url) return;
        try { await navigator.clipboard.writeText(url); say('StillHigh link copied.'); }
        catch (_) { say('Copy the selected transmission from your browser address bar.'); }
      });
      tv.querySelectorAll('[data-sh-tv-return]').forEach((button) => button.addEventListener('click', () => {
        consoleAbove = false;
        screen.classList.remove('is-docked');
        tv.classList.remove('is-player-docked');
        if (miniBar) miniBar.hidden = true;
        consoleEl.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      }));
      tv.querySelectorAll('[data-sh-tv-stop]').forEach((button) => button.addEventListener('click', () => {
        dockDismissed = true;
        if (shuffleActive) stopShuffleMode();
        else {
          stopPlayback();
          say('Transmission stopped.');
        }
      }));
      consoleEl.addEventListener('keydown', (event) => {
        if (event.defaultPrevented || event.repeat || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey || event.target.closest('button,a,input,textarea,select,[contenteditable],[role="textbox"]')) return;
        if (event.key === 'ArrowLeft') { event.preventDefault(); shuffleActive ? previousShuffle() : move(-1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); shuffleActive ? advanceShuffle('next') : move(1); }
        if (event.key === ' ') { event.preventDefault(); playCurrent(); }
        if (event.key.toLowerCase() === 's') { event.preventDefault(); saveButton?.click(); }
        if (event.key.toLowerCase() === 'r') { event.preventDefault(); shuffleActive ? advanceShuffle('random') : random(); }
      });

      let initial = cards[0];
      try {
        const prior = sessionStorage.getItem('stillhigh-tv-last');
        initial = cards.find((card) => card.dataset.shVideoId === prior) || initial;
      } catch (_) {}
      tv.dataset.shInitialId = initial.dataset.shVideoId;
      const linkedId = new URLSearchParams(window.location.search).get('transmission');
      const linkedCard = cards.find(card => card.dataset.shVideoId === linkedId) || appendCatalogItem(catalogById.get(linkedId));
      if (linkedCard) { initial = linkedCard; archiveExpanded = true; }
      selectCard(initial);
      applyFilter('all', { autoSelect: false });
      tv.dataset.shHistoryReady = 'true';
      tv._shSelectId = (id, options = {}) => {
        const target = cards.find(card => card.dataset.shVideoId === id) || appendCatalogItem(catalogById.get(id));
        if (!target) return false;
        archiveExpanded = true;
        applyFilter(target.dataset.shCategory || 'all', { autoSelect: false });
        selectCard(target, { ...options, play: false, scroll: false });
        return true;
      };
      refreshSaved();
      renderShuffleState();
      tv._shTune = tuneFrequency;
      window.dispatchEvent(new CustomEvent('stillhigh:tvready'));
      tv._shCleanup = () => {
        stopShuffleMode({ keepPlayback: true, announce: false });
        stopPlayback();
        consoleObserver?.disconnect();
        window.removeEventListener('resize', handleViewport);
        document.removeEventListener('keydown', handleShuffleHotkey);
        screen.removeEventListener('touchstart', handleTouchStart);
        screen.removeEventListener('touchend', handleTouchEnd);
        screen.removeEventListener('touchcancel', handleTouchCancel);
        screen.removeEventListener('click', suppressSwipeClick, true);
        window.clearTimeout(toastTimer);
        window.clearTimeout(tuningTimer);
        window.clearTimeout(shuffleAdvanceTimer);
        window.clearTimeout(shuffleMagicTimer);
        window.clearTimeout(shuffleArrivalTimer);
        window.clearTimeout(shuffleErrorTimer);
        window.clearTimeout(shufflePlaybackStartTimer);
        window.clearTimeout(youtubeReadyTimer);
        tv.classList.remove('is-shuffle-igniting');
        pruneTransientShuffleCard({ restoreFocus: false });
        youtubeAttachEpoch += 1;
        try { youtubePlayer?.destroy?.(); } catch (_) {}
        youtubePlayer = null;
        youtubePlayerReady = false;
        youtubePlayerPromise = null;
        [
          ['--sh-live-accent', initialRootPalette.accent],
          ['--sh-live-accent-2', initialRootPalette.accentTwo],
          ['--sh-live-glow', initialRootPalette.glow]
        ].forEach(([property, value]) => {
          if (value) rootElement.style.setProperty(property, value);
          else rootElement.style.removeProperty(property);
        });
        if (initialRootPalette.signal === undefined) delete rootElement.dataset.shSignal;
        else rootElement.dataset.shSignal = initialRootPalette.signal;
        if (initialRootPalette.mood === undefined) delete rootElement.dataset.shMood;
        else rootElement.dataset.shMood = initialRootPalette.mood;
        window.dispatchEvent(new CustomEvent('stillhigh:moodchange', { detail: { key: initialRootPalette.mood || null, label: 'SIGNAL OPEN' } }));
        delete tv._shTune;
      };
    });
  };

  const initLegacyPortals = (root) => {
    findAll(root, '[data-sh-youtube-portal]').forEach((portal) => {
      if (portal.dataset.youtubeReady === 'true') return;
      portal.dataset.youtubeReady = 'true';
      const cards = [...portal.querySelectorAll('[data-sh-youtube-open]')];
      const dialog = portal.querySelector('[data-sh-youtube-dialog]');
      const player = dialog?.querySelector('.sh-youtube-dialog__player');
      const frame = dialog?.querySelector('[data-sh-youtube-frame]');
      const title = dialog?.querySelector('[data-sh-youtube-title]');
      const category = dialog?.querySelector('[data-sh-youtube-category]');
      const youtubeLink = dialog?.querySelector('[data-sh-youtube-link]');
      const reimagineLink = dialog?.querySelector('[data-sh-youtube-reimagine]');
      const hint = dialog?.querySelector('[data-sh-youtube-hint]');
      const closeDialog = () => {
        if (!dialog) return;
        if (dialog.open && dialog.close) dialog.close(); else dialog.removeAttribute('open');
        if (frame) frame.src = '';
      };
      const openCard = (card) => {
        if (!dialog || !frame || !card) return;
        const id = card.dataset.shVideoId;
        if (!id) return;
        const origin = encodeURIComponent(window.location.origin);
        const referrer = encodeURIComponent(window.location.href.split('#')[0]);
        frame.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0&playsinline=1&origin=${origin}&widget_referrer=${referrer}`;
        const videoUrl = card.dataset.shVideoUrl || `https://www.youtube.com/watch?v=${encodeURIComponent(id)}`;
        const isShort = card.dataset.shVideoFormat === 'short';
        const canReimagine = card.dataset.shReimagineReady === 'true';
        player?.classList.toggle('is-short', isShort);
        if (title) title.textContent = card.dataset.shVideoTitle || 'StillHigh';
        if (category) category.textContent = card.dataset.shVideoCategory || 'STILLHIGH TRANSMISSION';
        if (youtubeLink) youtubeLink.href = videoUrl;
        if (reimagineLink) { reimagineLink.href = videoUrl; reimagineLink.hidden = !canReimagine; }
        if (hint) hint.textContent = canReimagine
          ? 'Watch here, then open this Short on YouTube and tap Remix, then Reimagine.'
          : 'Watch here, or open the official video on YouTube for the full channel experience.';
        if (dialog.showModal) dialog.showModal(); else dialog.setAttribute('open', '');
      };
      cards.forEach((card) => card.addEventListener('click', () => openCard(card)));
      portal.querySelectorAll('[data-sh-filter]').forEach((filter) => filter.addEventListener('click', () => {
        portal.querySelectorAll('[data-sh-filter]').forEach((item) => item.classList.remove('is-active'));
        filter.classList.add('is-active');
        const key = filter.dataset.shFilter;
        cards.forEach((card) => { card.hidden = key !== 'all' && card.dataset.shCategory !== key; });
      }));
      portal.querySelector('[data-sh-random-transmission]')?.addEventListener('click', () => {
        const visible = cards.filter((card) => !card.hidden);
        if (visible.length) openCard(visible[Math.floor(Math.random() * visible.length)]);
      });
      dialog?.querySelector('[data-sh-youtube-close]')?.addEventListener('click', closeDialog);
      dialog?.addEventListener('click', (event) => { if (event.target === dialog) closeDialog(); });
      dialog?.addEventListener('close', () => { if (frame) frame.src = ''; });
      portal._shCleanup = closeDialog;
    });
  };

  const initWorldRails = (root) => {
    findAll(root, '[data-sh-world-rail]').forEach((rail) => {
      if (rail.dataset.shWorldReady === 'true') return;
      rail.dataset.shWorldReady = 'true';
      const section = rail.closest('.sh-worlds');
      const cards = [...rail.querySelectorAll('[data-sh-world-card]')];
      const progress = section?.querySelector('[data-sh-world-progress]');
      const previous = section?.querySelector('[data-sh-world-prev]');
      const next = section?.querySelector('[data-sh-world-next]');
      if (!section || !cards.length) return;

      let focalIndex = 0;
      let sectionInView = !('IntersectionObserver' in window);
      let frameRequest = 0;
      const paintRecommendation = (mood = document.documentElement.dataset.shMood || '') => {
        cards.forEach((card) => card.classList.remove('is-recommended'));
        if (!mood) return;
        cards.find((card) => card.dataset.shWorldCategory === mood)?.classList.add('is-recommended');
      };
      const refreshPlayback = () => {
        cards.forEach((card, index) => {
          const video = card.querySelector('[data-sh-world-video],.sh-world-card__video');
          if (!video) return;
          const atmosphereOpen = document.documentElement.dataset.shAtmospherePaused !== 'true';
          const shouldPlay = atmosphereOpen && !reduceMotion && !saveData && sectionInView && !document.hidden && index === focalIndex;
          if (shouldPlay) video.play?.().catch(() => {});
          else video.pause?.();
        });
      };
      const paintFocal = () => {
        frameRequest = 0;
        const railBox = rail.getBoundingClientRect();
        const center = railBox.left + railBox.width / 2;
        let closestDistance = Number.POSITIVE_INFINITY;
        cards.forEach((card, index) => {
          const box = card.getBoundingClientRect();
          const distance = Math.abs(box.left + box.width / 2 - center);
          if (distance < closestDistance) {
            closestDistance = distance;
            focalIndex = index;
          }
        });
        cards.forEach((card, index) => card.classList.toggle('is-focal', index === focalIndex));
        if (progress) progress.textContent = `${String(focalIndex + 1).padStart(2, '0')} / ${String(cards.length).padStart(2, '0')}`;
        refreshPlayback();
      };
      const requestPaint = () => {
        if (!frameRequest) frameRequest = window.requestAnimationFrame(paintFocal);
      };
      const tuneWorld = (direction) => {
        focalIndex = (focalIndex + direction + cards.length) % cards.length;
        const card = cards[focalIndex];
        const left = card.offsetLeft - (rail.clientWidth - card.clientWidth) / 2;
        rail.scrollTo({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
      };
      const handleKey = (event) => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); tuneWorld(-1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); tuneWorld(1); }
      };
      const handleMood = (event) => paintRecommendation(event.detail?.key || '');
      const handleVisibility = () => refreshPlayback();
      const handleMotion = () => refreshPlayback();
      const observer = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
        sectionInView = Boolean(entries[0]?.isIntersecting);
        refreshPlayback();
      }, { rootMargin: '120px 0px', threshold: .12 }) : null;

      previous?.addEventListener('click', () => tuneWorld(-1));
      next?.addEventListener('click', () => tuneWorld(1));
      rail.addEventListener('scroll', requestPaint, { passive: true });
      rail.addEventListener('keydown', handleKey);
      window.addEventListener('resize', requestPaint, { passive: true });
      window.addEventListener('stillhigh:moodchange', handleMood);
      window.addEventListener('stillhigh:motionchange', handleMotion);
      document.addEventListener('visibilitychange', handleVisibility);
      observer?.observe(section);
      paintRecommendation();
      paintFocal();

      rail._shCleanup = () => {
        observer?.disconnect();
        if (frameRequest) window.cancelAnimationFrame(frameRequest);
        rail.removeEventListener('scroll', requestPaint);
        rail.removeEventListener('keydown', handleKey);
        window.removeEventListener('resize', requestPaint);
        window.removeEventListener('stillhigh:moodchange', handleMood);
        window.removeEventListener('stillhigh:motionchange', handleMotion);
        document.removeEventListener('visibilitychange', handleVisibility);
        cards.forEach((card) => card.querySelector('video')?.pause?.());
      };
    });
  };

  const initObservedVideos = (root) => {
    findAll(root, 'video[data-sh-observe], video.sh-stream-card__video, video.sh-lab__video, video.sh-newsletter__video').forEach((video) => {
      if (video.dataset.observed === 'true') return;
      video.dataset.observed = 'true';
      video.muted = true;
      video.playsInline = true;
      video.pause?.();
      if (!('IntersectionObserver' in window) || saveData || reduceMotion) return;
      let inView = false;
      const refreshPlayback = () => {
        const atmosphereOpen = document.documentElement.dataset.shAtmospherePaused !== 'true';
        if (atmosphereOpen && inView && !document.hidden) video.play?.().catch(() => {});
        else video.pause?.();
      };
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          inView = entry.isIntersecting;
          refreshPlayback();
        });
      }, { rootMargin: '120px 0px', threshold: .08 });
      observer.observe(video);
      const visibility = () => refreshPlayback();
      document.addEventListener('visibilitychange', visibility);
      window.addEventListener('stillhigh:motionchange', visibility);
      video._shCleanup = () => {
        observer.disconnect();
        document.removeEventListener('visibilitychange', visibility);
        window.removeEventListener('stillhigh:motionchange', visibility);
        video.pause?.();
      };
    });
  };

  const paintClocks = (root = document) => {
    const value = new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' }).format(new Date());
    findAll(root, '[data-sh-live-clock]').forEach((clock) => { clock.textContent = value; });
  };

  const boot = (root = document) => {
    initRotators(root);
    initPrompts(root);
    initReimagineLabs(root);
    initInteractiveTV(root);
    initLegacyPortals(root);
    initWorldRails(root);
    initObservedVideos(root);
    paintClocks(root);
  };

  const resolveShScrollTarget = (hash) => {
    const directTarget = document.getElementById(hash.slice(1));
    if (directTarget) return directTarget;

    const dynamicSectionTargets = [
      ['#sh-tv-console-', '[data-sh-tv-console]'],
      ['#sh-moodbar-', '[id^="sh-moodbar-"]'],
      ['#sh-tv-guide-', '[data-sh-tv-guide]'],
      ['#sh-interactive-tv-', '[data-sh-tv]'],
      ['#sh-worlds-', '[id^="sh-worlds-"]'],
      ['#sh-community-spotlight-', '[data-sh-community]'],
      ['#sh-reimagine-', '[data-sh-lab]'],
      ['#sh-artifacts-', '[id^="sh-artifacts-"]'],
    ];
    const destination = dynamicSectionTargets.find(([prefix]) => hash.startsWith(prefix));
    return destination ? document.querySelector(destination[1]) : null;
  };

  const initChapterNavigation = () => {
    if (document.documentElement.dataset.shChapterNavReady) return;
    const dock = document.querySelector('.sh-signal-dock');
    if (!dock || !('IntersectionObserver' in window)) return;
    const chapters = [...dock.querySelectorAll('a[href^="#sh-"]')]
      .map((link) => ({ link, target: resolveShScrollTarget(link.getAttribute('href')) }))
      .filter((item) => item.target);
    if (!chapters.length) return;
    document.documentElement.dataset.shChapterNavReady = 'true';
    const visible = new Map();
    const paint = () => {
      const winner = [...visible.entries()]
        .filter(([, entry]) => entry.isIntersecting)
        .sort((a, b) => b[1].intersectionRatio - a[1].intersectionRatio)[0]?.[0];
      chapters.forEach((chapter) => {
        if (chapter === winner) chapter.link.setAttribute('aria-current', 'location');
        else chapter.link.removeAttribute('aria-current');
      });
    };
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const chapter = chapters.find((item) => item.target === entry.target);
        if (chapter) visible.set(chapter, entry);
      });
      paint();
    }, { rootMargin: '-24% 0px -58%', threshold: [0, .08, .2, .4, .65] });
    chapters.forEach((chapter) => observer.observe(chapter.target));
    chapters[0].link.setAttribute('aria-current', 'location');
  };

  const globalEffects = () => {
    initChapterNavigation();
    if (!document.documentElement.dataset.shAnchorScrollReady) {
      document.documentElement.dataset.shAnchorScrollReady = 'true';
      document.addEventListener('click', (event) => {
        const anchor = event.target.closest?.('a[href^="#sh-"]');
        if (!anchor || event.defaultPrevented || event.button !== 0) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const hash = anchor.getAttribute('href');
        if (!hash) return;
        const target = resolveShScrollTarget(hash);
        if (!target) return;
        event.preventDefault();
        history.pushState(null, '', hash);
        if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
        target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
      });
    }
    if (!window.__stillhighClockTimer) {
      const alignClock = () => {
        paintClocks();
        window.__stillhighClockTimer = window.setTimeout(() => {
          paintClocks();
          window.__stillhighClockTimer = window.setInterval(paintClocks, 60000);
        }, 60000 - (Date.now() % 60000));
      };
      alignClock();
    }
    if (!document.documentElement.dataset.shTvJumpReady) {
      document.documentElement.dataset.shTvJumpReady = 'true';
      document.addEventListener('click', (event) => {
        const trigger = event.target.closest?.('[data-sh-tv-jump]');
        if (!trigger || event.defaultPrevented || event.button !== 0 || event.ctrlKey || event.metaKey || event.altKey || event.shiftKey) return;
        const tv = document.querySelector('[data-sh-tv]');
        if (!tv?._shTune) return;
        event.preventDefault();
        tv._shTune(trigger.dataset.shTvJump, trigger.dataset.shTvLead, { scroll: true });
      });
    }
    if (document.querySelector('.sh-gateway') && !document.querySelector('.sh-scroll-progress')) {
      const progress = document.createElement('div');
      progress.className = 'sh-scroll-progress';
      document.body.append(progress);
      let ticking = false;
      const update = () => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        progress.style.width = `${max > 0 ? (window.scrollY / max) * 100 : 0}%`;
        ticking = false;
      };
      const requestUpdate = () => {
        if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
      };
      update();
      window.addEventListener('scroll', requestUpdate, { passive: true });
      window.addEventListener('resize', requestUpdate);
    }
  };

  const init = () => { globalEffects(); boot(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    findAll(event.target, '[data-sh-rotator],[data-sh-tv],[data-sh-lab],[data-sh-youtube-portal],[data-sh-world-rail],video').forEach((item) => item._shCleanup?.());
  });
})();
